import { useCallback, useEffect, useRef, useState } from "react";
import Logo from "@/assets/images/Logo.png";
import { errorHandler } from "@/utils/errorhandler";
import {
  ResultadoOrcamento,
  SalvarOrcamentoResponse,
  TurmaPublica,
  UnidadePublica,
  getTurmasPublicas,
  getUnidadesPublicas,
  registrarInteresse,
  salvarOrcamentoPublico,
  simularOrcamento,
} from "@/services/orcamentoPublico";
import CatalogoDisciplinas from "./CatalogoDisciplinas";
import GateInteressado, { DadosInteressado } from "./GateInteressado";
import ResumoLateral from "./ResumoLateral";
import Sucesso from "./Sucesso";
import { OrcamentoGlobalStyle } from "./styles";
import { alunoSchema } from "./schema";

/** Chave por aba: um F5 não pode perder o lead nem duplicá-lo. */
const CHAVE_INTERESSE = "@MatriculaWeb:orcamentoPublico:interesse";

/** Espera entre marcar uma específica e pedir a simulação ao servidor. */
const DEBOUNCE_SIMULACAO_MS = 250;

type InteresseArmazenado = { id: number; dados: DadosInteressado };

/** Mesmos códigos, na mesma ordem — as listas vêm sempre ordenadas pela API. */
function mesmaLista(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((item, indice) => item === b[indice]);
}

function lerInteresseArmazenado(): InteresseArmazenado | null {
  try {
    const bruto = sessionStorage.getItem(CHAVE_INTERESSE);
    return bruto ? (JSON.parse(bruto) as InteresseArmazenado) : null;
  } catch {
    return null;
  }
}

function gravarInteresseArmazenado(valor: InteresseArmazenado | null) {
  try {
    if (valor) sessionStorage.setItem(CHAVE_INTERESSE, JSON.stringify(valor));
    else sessionStorage.removeItem(CHAVE_INTERESSE);
  } catch {
    // Aba anônima ou storage bloqueado: a página segue funcionando, só perde a
    // retomada depois de um reload.
  }
}

export function Orcamento() {
  const armazenado = useRef(lerInteresseArmazenado()).current;

  const [interesseId, setInteresseId] = useState<number | null>(armazenado?.id ?? null);
  const [interessado, setInteressado] = useState<DadosInteressado | null>(armazenado?.dados ?? null);
  const [registrandoInteresse, setRegistrandoInteresse] = useState(false);

  const [unidades, setUnidades] = useState<UnidadePublica[]>([]);
  const [unidadeId, setUnidadeId] = useState<number | null>(null);
  const [turmas, setTurmas] = useState<TurmaPublica[]>([]);
  const [turmaId, setTurmaId] = useState<number | null>(null);

  const [selecionados, setSelecionados] = useState<string[]>([]);
  /**
   * Monitorias que o usuário desmarcou depois de elas entrarem sozinhas. Sem
   * isto o servidor voltaria a incluí-las na simulação seguinte. Quem decide
   * quais recusas ainda valem é a API — a resposta devolve a lista filtrada.
   */
  const [recusados, setRecusados] = useState<string[]>([]);
  const [resultado, setResultado] = useState<ResultadoOrcamento | null>(null);
  const [simulando, setSimulando] = useState(false);

  const [alunoNome, setAlunoNome] = useState("");
  const [erroAluno, setErroAluno] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [resposta, setResposta] = useState<SalvarOrcamentoResponse | null>(null);

  /** Descarta resposta de simulação que não é a da última requisição. */
  const simulacaoAtual = useRef(0);

  useEffect(() => {
    getUnidadesPublicas()
      .then((retorno) => setUnidades(retorno.data ?? []))
      .catch(errorHandler);
  }, []);

  useEffect(() => {
    if (!unidadeId) {
      setTurmas([]);
      return;
    }

    getTurmasPublicas(unidadeId)
      .then((retorno) => setTurmas(retorno.data ?? []))
      .catch(errorHandler);
  }, [unidadeId]);

  // A tela nunca calcula: a cada mudança de seleção pede o resultado ao
  // servidor, que é o mesmo cálculo usado na hora de gravar.
  useEffect(() => {
    if (!unidadeId || !turmaId) {
      setResultado(null);
      return;
    }

    const requisicao = ++simulacaoAtual.current;
    setSimulando(true);

    const temporizador = setTimeout(() => {
      simularOrcamento({ unidadeId, turmaId, itens: selecionados, itensRecusados: recusados })
        .then((retorno) => {
          if (requisicao !== simulacaoAtual.current) return;
          setResultado(retorno.data);
          // A API descarta a recusa cuja específica vinculada saiu da seleção —
          // é o que faz a monitoria voltar quando a específica é remarcada.
          // Mantendo a referência quando a lista não muda, a resposta não
          // realimenta o efeito (que depende de `recusados`) num laço.
          const vigentes: string[] = retorno.data?.itensRecusados ?? [];
          setRecusados((atuais) => (mesmaLista(atuais, vigentes) ? atuais : vigentes));
        })
        .catch((erro) => {
          if (requisicao !== simulacaoAtual.current) return;
          errorHandler(erro);
        })
        .finally(() => {
          if (requisicao === simulacaoAtual.current) setSimulando(false);
        });
    }, DEBOUNCE_SIMULACAO_MS);

    return () => clearTimeout(temporizador);
  }, [unidadeId, turmaId, selecionados, recusados]);

  const registrar = useCallback(async (dados: DadosInteressado) => {
    setRegistrandoInteresse(true);
    try {
      const retorno = await registrarInteresse(dados);
      const id = retorno.data?.interesseId ?? null;
      setInteresseId(id);
      setInteressado(dados);
      if (id) gravarInteresseArmazenado({ id, dados });
    } catch (erro) {
      errorHandler(erro);
    } finally {
      setRegistrandoInteresse(false);
    }
  }, []);

  const editarInteressado = useCallback(() => {
    // O id é mantido: o registro é upsert por e-mail, então reenviar atualiza a
    // mesma linha em vez de criar outra.
    setInteressado(null);
  }, []);

  const escolherUnidade = (novaUnidadeId: number) => {
    if (novaUnidadeId === unidadeId) return;
    setUnidadeId(novaUnidadeId);
    // Turma e seleção pertencem à unidade anterior: o preço muda com o fator.
    setTurmaId(null);
    setSelecionados([]);
    setRecusados([]);
  };

  const escolherTurma = (novaTurmaId: number) => {
    if (novaTurmaId === turmaId) return;
    setTurmaId(novaTurmaId);
    setSelecionados([]);
    setRecusados([]);
  };

  const alternarItem = (codigo: string) => {
    const linha = resultado?.linhas.find((item) => item.codigo === codigo);

    // Desmarcar o que entrou sozinho (monitoria) não é tirar da seleção — o
    // código nunca esteve nela. É recusar, senão o servidor volta a incluir.
    if (linha?.selecionado && linha.selecionadoAutomaticamente) {
      setRecusados((atuais) => (atuais.includes(codigo) ? atuais : [...atuais, codigo]));
      return;
    }

    if (linha?.selecionado) {
      setSelecionados((atuais) => atuais.filter((item) => item !== codigo));
      return;
    }

    setSelecionados((atuais) => (atuais.includes(codigo) ? atuais : [...atuais, codigo]));
    // Marcar na mão desfaz a recusa.
    setRecusados((atuais) => atuais.filter((item) => item !== codigo));
  };

  const enviar = async () => {
    const validacao = alunoSchema.safeParse({ alunoNome });
    if (!validacao.success) {
      setErroAluno(validacao.error.issues[0]?.message ?? "Informe o nome do aluno.");
      return;
    }
    if (!interessado || !unidadeId || !turmaId) return;

    setErroAluno(null);
    setEnviando(true);
    try {
      const retorno = await salvarOrcamentoPublico({
        interesseId,
        ...interessado,
        unidadeId,
        turmaId,
        itens: selecionados,
        itensRecusados: recusados,
        alunoNome: validacao.data.alunoNome,
      });
      setResposta(retorno.data);
      // O lead virou orçamento: um próximo acesso começa um interesse novo.
      gravarInteresseArmazenado(null);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (erro) {
      errorHandler(erro);
    } finally {
      setEnviando(false);
    }
  };

  const recomecar = () => {
    setResposta(null);
    setInteresseId(null);
    setInteressado(null);
    setUnidadeId(null);
    setTurmaId(null);
    setSelecionados([]);
    setRecusados([]);
    setResultado(null);
    setAlunoNome("");
    setErroAluno(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const liberado = Boolean(interessado);
  const temSelecao = Boolean(resultado && resultado.linhas.some((linha) => linha.selecionado));
  const unidadeEscolhida = unidades.find((unidade) => unidade.id === unidadeId);
  const turmaEscolhida = turmas.find((turma) => turma.id === turmaId);

  return (
    <div className="orcamento-publico">
      <OrcamentoGlobalStyle />
      <Cabecalho />

      <div style={{ maxWidth: 1180, margin: "0 auto", padding: "0 20px 80px" }}>
        {resposta && interessado ? (
          <>
            <Hero
              eyebrow="Pronto"
              titulo="Orçamento gerado."
              descricao="Seu orçamento já está registrado. Imprima ou guarde o número."
            />
            <Sucesso
              resposta={resposta}
              interessadoNome={interessado.nome}
              interessadoEmail={interessado.email}
              alunoNome={alunoNome}
              onNovoOrcamento={recomecar}
            />
          </>
        ) : (
          <>
            <Hero
              eyebrow="Sem compromisso · resposta na hora"
              titulo="Monte seu orçamento em dois minutos."
              descricao="Deixe seu contato, escolha a unidade, a turma e as específicas que quer cursar. O valor aparece na hora, já com os descontos automáticos da turma."
            />

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) minmax(280px, 340px)",
                gap: 34,
                alignItems: "start",
              }}
              className="op-colunas"
            >
              <main>
                <Passo numero={1} titulo="Vamos começar">
                  <GateInteressado
                    registrado={interessado}
                    enviando={registrandoInteresse}
                    onRegistrar={registrar}
                    onEditar={editarInteressado}
                  />
                </Passo>

                <Passo
                  numero={2}
                  titulo="Onde você quer estudar?"
                  dica={liberado ? undefined : "Preencha seus dados"}
                  bloqueado={!liberado}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                      gap: 10,
                    }}
                  >
                    {unidades.map((unidade) => (
                      <button
                        key={unidade.id}
                        type="button"
                        className="op-opt"
                        aria-pressed={unidadeId === unidade.id}
                        onClick={() => escolherUnidade(unidade.id)}
                      >
                        <div style={{ fontWeight: 700, fontSize: 15.5 }}>{unidade.nome}</div>
                        {unidade.enderecoCompleto && (
                          <div style={{ fontSize: 13.5, color: "var(--op-muted)", marginTop: 3 }}>
                            {unidade.enderecoCompleto}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </Passo>

                <Passo
                  numero={3}
                  titulo="Escolha a turma"
                  dica={unidadeId ? `${turmas.length} turmas disponíveis` : "Selecione uma unidade"}
                  bloqueado={!liberado || !unidadeId}
                >
                  {/* Sem unidade escolhida a lista está vazia por natureza: dizer
                      "nenhuma turma nesta unidade" ali seria mentira. */}
                  {unidadeId && turmas.length === 0 ? (
                    <div
                      className="op-panel"
                      style={{ padding: "32px 20px", textAlign: "center", color: "var(--op-faint)" }}
                    >
                      Nenhuma turma disponível nesta unidade.
                    </div>
                  ) : (
                    <div style={{ display: "grid", gap: 10 }}>
                      {turmas.map((turma) => (
                        <button
                          key={turma.id}
                          type="button"
                          className="op-opt"
                          aria-pressed={turmaId === turma.id}
                          onClick={() => escolherTurma(turma.id)}
                        >
                          <div style={{ fontWeight: 700, fontSize: 15.5 }}>{turma.nome}</div>
                          {turma.periodoLetivo && (
                            <div style={{ fontSize: 13.5, color: "var(--op-muted)", marginTop: 3 }}>
                              {turma.periodoLetivo}
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </Passo>

                <Passo
                  numero={4}
                  titulo="Monte sua grade"
                  dica={turmaId ? "Marque o que quiser cursar" : "Selecione uma turma"}
                  bloqueado={!liberado || !turmaId}
                >
                  <CatalogoDisciplinas
                    resultado={resultado}
                    carregando={simulando}
                    onAlternar={alternarItem}
                  />
                </Passo>

                <Passo
                  numero={5}
                  titulo="Quem vai estudar?"
                  dica={temSelecao ? "Falta só isso" : "Selecione ao menos uma específica"}
                  bloqueado={!liberado || !temSelecao}
                >
                  <div className="op-panel">
                    <div style={{ padding: 18 }}>
                      <label
                        htmlFor="op-aluno"
                        style={{
                          display: "block",
                          fontSize: 12,
                          fontWeight: 700,
                          letterSpacing: ".05em",
                          textTransform: "uppercase",
                          color: "var(--op-muted)",
                          marginBottom: 5,
                        }}
                      >
                        Nome do aluno
                      </label>
                      <input
                        id="op-aluno"
                        className="op-inp"
                        value={alunoNome}
                        onChange={(evento) => setAlunoNome(evento.target.value)}
                        placeholder="Pedro Ribeiro de Souza"
                        aria-invalid={erroAluno ? "true" : undefined}
                      />
                      {erroAluno && (
                        <div style={{ color: "var(--op-crimson)", fontSize: 13, marginTop: 4 }}>
                          {erroAluno}
                        </div>
                      )}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 14,
                        alignItems: "center",
                        padding: 18,
                        borderTop: "1px solid var(--op-line)",
                      }}
                    >
                      <button
                        type="button"
                        className="op-btn op-btn-primary"
                        onClick={enviar}
                        disabled={enviando}
                      >
                        {enviando ? "Gerando..." : "Gerar meu orçamento"}
                      </button>
                      <span style={{ fontSize: 13, color: "var(--op-faint)", flex: 1, minWidth: 200 }}>
                        A secretaria entra em contato sobre este orçamento pelo contato que você
                        informou.
                      </span>
                    </div>
                  </div>
                </Passo>
              </main>

              {/*
                O sticky fica no próprio <aside>, não num filho: como o grid usa
                `align-items: start`, o aside tem a altura do conteúdo e um filho
                sticky não teria por onde deslizar. Como item do grid, o bloco de
                contenção dele passa a ser o container inteiro, que é alto.
                A rolagem própria evita que o "12x de" fique fora de alcance em
                tela baixa — mesmo recurso da coluna lateral da tela de matrícula.
              */}
              <aside
                style={{
                  position: "sticky",
                  top: 20,
                  maxHeight: "calc(100vh - 40px)",
                  overflowY: "auto",
                }}
              >
                <ResumoLateral
                  unidadeNome={unidadeEscolhida?.nome}
                  turmaNome={turmaEscolhida?.nome}
                  resultado={resultado}
                />
              </aside>
            </div>
          </>
        )}
      </div>

      <style>{`
        @media (max-width: 940px) {
          .orcamento-publico .op-colunas { grid-template-columns: minmax(0, 1fr) !important; gap: 26px !important; }
          .orcamento-publico .op-colunas aside {
            position: static !important;
            max-height: none !important;
            overflow: visible !important;
          }
        }
      `}</style>
    </div>
  );
}

function Cabecalho() {
  return (
    <header
      style={{
        background: "var(--op-surface)",
        borderBottom: "1px solid var(--op-line)",
      }}
    >
      <div
        style={{
          maxWidth: 1180,
          margin: "0 auto",
          padding: "10px 20px",
          display: "flex",
          alignItems: "center",
          gap: 14,
        }}
      >
        <img src={Logo} alt="Academia das Específicas" style={{ height: 38, width: "auto" }} />
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 12.5, color: "var(--op-muted)" }}>Orçamento online</span>
      </div>
    </header>
  );
}

function Hero({
  eyebrow,
  titulo,
  descricao,
}: {
  eyebrow: string;
  titulo: string;
  descricao: string;
}) {
  return (
    <div style={{ padding: "44px 0 30px", maxWidth: 640 }}>
      <p
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: ".2em",
          textTransform: "uppercase",
          color: "var(--op-crimson)",
          margin: "0 0 14px",
        }}
      >
        {eyebrow}
      </p>
      <h1 style={{ fontSize: "clamp(30px, 5vw, 44px)", lineHeight: 1.05, margin: 0, fontWeight: 800 }}>
        {titulo}
      </h1>
      <p style={{ color: "var(--op-muted)", fontSize: 17, margin: "14px 0 0" }}>{descricao}</p>
    </div>
  );
}

function Passo({
  numero,
  titulo,
  dica,
  bloqueado,
  children,
}: {
  numero: number;
  titulo: string;
  dica?: string;
  bloqueado?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section className="op-step" data-bloqueado={bloqueado ? "true" : "false"} style={{ marginBottom: 30 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
        <span
          style={{
            fontWeight: 800,
            fontSize: 13,
            color: "var(--op-indigo)",
            background: "var(--op-indigo-wash)",
            borderRadius: 6,
            padding: "3px 8px",
          }}
        >
          {numero}
        </span>
        <h2 style={{ fontSize: 21, fontWeight: 800, margin: 0 }}>{titulo}</h2>
        {dica && (
          <span style={{ fontSize: 14, color: "var(--op-faint)", marginLeft: "auto" }}>{dica}</span>
        )}
      </div>
      {children}
    </section>
  );
}

export default Orcamento;
